import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { track } from "@supersig/state";
import { createNodeId, SWStateRecordType, paywallId, ReRouteBackButtonValues } from "@superwall-me/schema";
import React, { useMemo, useState, useRef, useCallback } from "react";
import tw from "tailwind-styled-components";
import { EditorForValueType, flattenTree, SButton, SIcon, SInputV2, SVariablePicker, SWAttributeKeyPicker, useEditorAPI, useEditorDependencyInjectionContext } from "../..";
import { SDropdownV3 } from "../../SDropdownV3";
import { startCase } from "es-toolkit";
const ActionWrapper = tw.div`
bg-grayscale-dark rounded-xl border border-white/10
`;
const ActionWrapperInner = tw.div`
grid grid-cols-[auto,1fr] gap-2 items-center p-2
`;
export const ClickBehaviorEditor = ({ readOnly, value, onChangeValue, onClickValue })=>{
    const { getPaywall, getApplicationPlatform } = useEditorAPI();
    const paywall = getPaywall && getPaywall(paywallId);
    const applicationPlatform = getApplicationPlatform && getApplicationPlatform();
    const animationOptions = [
        {
            label: "Shrink",
            value: "scale-in-out"
        },
        {
            label: "Grow",
            value: "scale-out-in"
        },
        {
            label: "Fade",
            value: "fade-out-in"
        },
        {
            label: "None",
            value: "none"
        }
    ];
    const gameControllerOptions = [
        {
            label: "A Button",
            value: "a"
        },
        {
            label: "B Button",
            value: "b"
        },
        {
            label: "X Button",
            value: "x"
        },
        {
            label: "Y Button",
            value: "y"
        },
        {
            label: "L1 Button",
            value: "l1"
        },
        {
            label: "L2 Button",
            value: "l2"
        },
        {
            label: "R1 Button",
            value: "r1"
        },
        {
            label: "R2 Button",
            value: "r2"
        },
        {
            label: "Menu Button",
            value: "menu"
        },
        {
            label: "Options Button",
            value: "options"
        }
    ];
    const clickActions = value?.clickActions;
    const onChange = (v)=>{
        let defaults = {
            type: "property-click-behavior",
            clickActions: [],
            animation: "none",
            gameControllerButton: undefined,
            backButton: undefined
        };
        const newValue = {
            ...defaults,
            ...value,
            ...v
        };
        onChangeValue && onChangeValue(newValue);
    };
    const editExecution = (index, execution)=>{
        let executions = value?.clickActions || [];
        const clickActions = [
            ...executions.slice(0, index),
            {
                type: "action-execution",
                action: execution,
                id: executions[index].id
            },
            ...executions.slice(index + 1)
        ];
        onChange({
            ...value,
            clickActions
        });
    };
    const addExecution = ()=>{
        const executions = value?.clickActions || [];
        const id = "action:" + Math.random().toString(36).substring(7);
        onChange({
            ...value,
            clickActions: [
                ...executions,
                {
                    type: "action-execution",
                    id,
                    action: {
                        type: "purchase",
                        reference: {
                            type: "by-index",
                            index: 0
                        }
                    }
                }
            ]
        });
        // set it open and all others closed
        setOpenMap({
            ...Object.fromEntries(executions.map((action)=>{
                return [
                    action.id,
                    false
                ];
            })),
            [id]: true
        });
    };
    const removeExecution = (index)=>{
        const executions = value?.clickActions || [];
        onChange({
            ...value,
            clickActions: [
                ...executions.slice(0, index),
                ...executions.slice(index + 1)
            ]
        });
        // remove it from openMap
        const id = executions[index].id;
        const newOpenMap = {
            ...openMap
        };
        delete newOpenMap[id];
        setOpenMap(newOpenMap);
    };
    const [openMap, setOpenMap] = useState(Object.fromEntries((clickActions || []).map((action)=>[
            action.id,
            false
        ])));
    const allClosed = useMemo(()=>{
        return value?.clickActions?.every((action)=>!openMap[action.id]);
    }, [
        openMap,
        value?.clickActions
    ]);
    return /*#__PURE__*/ _jsx("div", {
        onClick: onClickValue,
        children: /*#__PURE__*/ _jsx("div", {
            className: readOnly ? "pointer-events-none" : "",
            children: /*#__PURE__*/ _jsxs("div", {
                className: "grid grid-cols-1 gap-2 items-center justify-stretch",
                children: [
                    clickActions?.map((execution, index)=>{
                        return /*#__PURE__*/ _jsx("div", {
                            className: `transition ${allClosed || openMap[execution.id] ? "" : "opacity-40"}`,
                            children: /*#__PURE__*/ _jsx(ActionExecutionEditor, {
                                execution: execution,
                                open: openMap[execution.id],
                                platform: applicationPlatform,
                                setOpen: (open)=>{
                                    setOpenMap({
                                        ...openMap,
                                        [execution.id]: open
                                    });
                                },
                                onChange: (action)=>{
                                    editExecution(index, action);
                                },
                                onDelete: ()=>{
                                    removeExecution(index);
                                }
                            })
                        }, execution.id);
                    }),
                    clickActions && clickActions.length > 0 ? /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: `transition ${allClosed ? "" : "opacity-40"}`,
                                children: /*#__PURE__*/ _jsx(ActionWrapper, {
                                    children: /*#__PURE__*/ _jsxs(ActionWrapperInner, {
                                        children: [
                                            /*#__PURE__*/ _jsx(Label, {
                                                children: "Animation"
                                            }),
                                            /*#__PURE__*/ _jsx(SDropdownV3, {
                                                borderless: false,
                                                placeholder: "None",
                                                options: animationOptions,
                                                value: value?.animation,
                                                onChange: (animation)=>{
                                                    onChange({
                                                        ...value,
                                                        animation: animation.value
                                                    });
                                                }
                                            })
                                        ]
                                    })
                                })
                            }),
                            paywall?.gameControllerSupport === "ENABLED" && /*#__PURE__*/ _jsx("div", {
                                className: `transition ${allClosed ? "" : "opacity-40"}`,
                                children: /*#__PURE__*/ _jsx(ActionWrapper, {
                                    children: /*#__PURE__*/ _jsxs(ActionWrapperInner, {
                                        children: [
                                            /*#__PURE__*/ _jsx(Label, {
                                                children: "Controller"
                                            }),
                                            /*#__PURE__*/ _jsx(SDropdownV3, {
                                                borderless: false,
                                                placeholder: "None",
                                                options: gameControllerOptions,
                                                value: value?.gameControllerButton,
                                                onChange: (option)=>{
                                                    onChange({
                                                        ...value,
                                                        gameControllerButton: option.value
                                                    });
                                                }
                                            })
                                        ]
                                    })
                                })
                            }),
                            paywall?.reRouteBackButton === "ENABLED" && /*#__PURE__*/ _jsx("div", {
                                className: `transition ${allClosed ? "" : "opacity-40"}`,
                                children: /*#__PURE__*/ _jsx(ActionWrapper, {
                                    children: /*#__PURE__*/ _jsxs(ActionWrapperInner, {
                                        children: [
                                            /*#__PURE__*/ _jsx(Label, {
                                                children: "On Back"
                                            }),
                                            /*#__PURE__*/ _jsx(SDropdownV3, {
                                                borderless: false,
                                                placeholder: "None",
                                                value: value?.backButton ? "ENABLED" : "DISABLED",
                                                options: [
                                                    ...ReRouteBackButtonValues.map((value)=>{
                                                        return {
                                                            label: startCase(value.toLowerCase()),
                                                            value
                                                        };
                                                    })
                                                ],
                                                onChange: (option)=>{
                                                    onChange({
                                                        ...value,
                                                        backButton: option.value === "ENABLED"
                                                    });
                                                }
                                            })
                                        ]
                                    })
                                })
                            })
                        ]
                    }) : undefined,
                    /*#__PURE__*/ _jsx("div", {
                        className: "grid grid-cols-1 items-center justify-center",
                        children: /*#__PURE__*/ _jsx(SButton, {
                            className: "col-span-2",
                            onClick: addExecution,
                            children: "+ Add Action"
                        })
                    })
                ]
            })
        })
    });
};
export const ActionExecutionEditor = ({ execution, onChange, onDelete, open, setOpen, platform })=>{
    const { featuresEnabled } = useEditorAPI();
    const actions = [
        "purchase",
        "set-state",
        "set-product-index",
        "close",
        "restore",
        "open-url",
        "request-store-review",
        "custom-in-app",
        "custom-placement",
        "set-attribute"
    ];
    // Filter actions based on platform
    let filteredActions = platform === "STRIPE" ? actions.filter((action)=>action !== "custom-in-app") : actions;
    // Filter actions based on feature flags
    if (!featuresEnabled?.("request_review_click_behavior")) {
        filteredActions = filteredActions.filter((action)=>action !== "request-store-review");
    }
    // gets an [action]: startCase(action) dictionary
    const labels = {
        ...filteredActions.reduce((acc, action)=>{
            return {
                ...acc,
                [action]: startCase(action)
            };
        }, {}),
        "set-state": "Update Variable",
        "set-product-index": "Select Product",
        "custom-in-app": "Custom Action",
        "set-attribute": "Set Attribute",
        ...platform === "STRIPE" ? {
            close: "Back"
        } : {}
    };
    const actionOptions = filteredActions.map((action)=>{
        return {
            label: labels[action],
            value: action
        };
    });
    const changeActionType = (actionType)=>{
        switch(actionType){
            case "open-url":
                onChange({
                    type: "open-url",
                    url: "",
                    urlType: "in-app-browser",
                    rendering: {
                        type: "literal"
                    }
                });
                break;
            case "close":
                onChange({
                    type: "close"
                });
                break;
            case "restore":
                onChange({
                    type: "restore"
                });
                break;
            case "custom-in-app":
                onChange({
                    type: "custom-in-app",
                    data: ""
                });
                break;
            case "purchase":
                onChange({
                    type: "purchase",
                    reference: {
                        type: "by-index",
                        index: 0
                    }
                });
                break;
            case "set-state":
                onChange({
                    type: "set-state",
                    stateId: "state:",
                    operation: undefined,
                    value: undefined
                });
                break;
            case "set-product-index":
                onChange({
                    type: "set-product-index",
                    index: 0
                });
                break;
            case "custom-placement":
                onChange({
                    type: "custom-placement",
                    name: "NewPlacement"
                });
                break;
            case "request-store-review":
                onChange({
                    type: "request-store-review",
                    reviewType: "in-app"
                });
                break;
            case "set-attribute":
                onChange({
                    type: "set-attribute",
                    attributes: [
                        {
                            key: "",
                            value: ""
                        }
                    ]
                });
                break;
            default:
                break;
        }
    };
    return /*#__PURE__*/ _jsx("span", {
        className: !open ? "cursor-pointer" : "",
        onClick: ()=>{
            if (!open) {
                setOpen(true);
            }
        },
        children: /*#__PURE__*/ _jsx(ActionWrapper, {
            children: open ? /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsxs(ActionWrapperInner, {
                        children: [
                            /*#__PURE__*/ _jsx(Label, {
                                children: "Action"
                            }),
                            /*#__PURE__*/ _jsx(SDropdownV3, {
                                borderless: false,
                                placeholder: "None",
                                options: actionOptions,
                                value: actionOptions.find((e)=>e.value == execution.action.type),
                                onChange: (value)=>{
                                    changeActionType(value.value);
                                }
                            }),
                            /*#__PURE__*/ _jsx(InnerActionEditor, {
                                action: execution.action,
                                onChange: onChange,
                                platform: platform
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "col-span-2 h-[1px] bg-white/10"
                    }),
                    /*#__PURE__*/ _jsx(ActionWrapperInner, {
                        children: /*#__PURE__*/ _jsxs("div", {
                            className: "grid grid-cols-2 col-span-2 items-center justify-center gap-2",
                            children: [
                                /*#__PURE__*/ _jsx(SButton, {
                                    className: "col-span-1",
                                    variant: "secondary",
                                    onClick: onDelete,
                                    children: "Delete"
                                }),
                                /*#__PURE__*/ _jsx(SButton, {
                                    className: "col-span-1",
                                    onClick: ()=>{
                                        setOpen(false);
                                    },
                                    children: "Done"
                                })
                            ]
                        })
                    })
                ]
            }) : /*#__PURE__*/ _jsx(ActionWrapperInner, {
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "grid col-span-2 grid-cols-[auto,1fr] items-center",
                    children: [
                        /*#__PURE__*/ _jsx(ActionDescription, {
                            action: execution.action
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            className: "flex flex-row items-center justify-end",
                            children: /*#__PURE__*/ _jsx(SButton, {
                                isMinimal: true,
                                className: "!p-2 !h-[34px] !w-[34px] flex items-center justify-center",
                                onClick: ()=>setOpen(true),
                                children: /*#__PURE__*/ _jsx(SIcon, {
                                    type: "kebab",
                                    className: "text-white/50",
                                    size: 16
                                })
                            })
                        })
                    ]
                })
            })
        })
    });
};
const ActionDescription = track(function ActionDescription({ action }) {
    const { products: { getProductReferenceNames } } = useEditorAPI();
    const referenceNames = getProductReferenceNames().get();
    if (!action) {
        return /*#__PURE__*/ _jsx(_Fragment, {});
    }
    const templateValue = (value)=>{
        // if it's a string, return in quotes
        if (typeof value == "string") {
            return `"${value}"`;
        }
        // if it's a boolean, return as is
        if (typeof value == "boolean") {
            return value ? "true" : "false";
        }
        // if it's a number, return as is
        if (typeof value == "number") {
            return value.toString();
        }
        return "";
    };
    let description = (()=>{
        switch(action.type){
            case "open-url":
                return {
                    icon: /*#__PURE__*/ _jsx(SIcon, {
                        type: "open",
                        size: 16
                    }),
                    title: `Open URL — ${startCase(action.urlType)}`,
                    subtitle: action.url || undefined
                };
            case "close":
                return {
                    icon: /*#__PURE__*/ _jsx(SIcon, {
                        type: "x",
                        size: 16
                    }),
                    title: "Close",
                    subtitle: "Close the paywall"
                };
            case "restore":
                return {
                    icon: /*#__PURE__*/ _jsx(SIcon, {
                        type: "refresh",
                        size: 16
                    }),
                    title: "Restore",
                    subtitle: "Restore purchases to the user"
                };
            case "custom-in-app":
                return {
                    icon: /*#__PURE__*/ _jsx(SIcon, {
                        type: "play",
                        size: 16
                    }),
                    title: "Custom Action",
                    subtitle: `Name: ${action.data}`
                };
            case "custom-placement":
                return {
                    icon: /*#__PURE__*/ _jsx(SIcon, {
                        type: "bolt",
                        size: 16
                    }),
                    title: "Custom Placement",
                    subtitle: `${action.name}`
                };
            case "purchase":
                const reference = action.reference;
                const product = reference;
                let productLabel = product.type == "by-index" ? `Purchase ${getOrdinal(product.index + 1)} - ${startCase(referenceNames[product.index] ?? "")}` : "Purchase Selected Product";
                if (product.type === "by-index" && product.index >= referenceNames.length) {
                    productLabel = `Purchase ${getOrdinal(product.index + 1)} Product (Missing)`;
                }
                return {
                    icon: /*#__PURE__*/ _jsx(SIcon, {
                        type: "checkout",
                        size: 16
                    }),
                    title: productLabel,
                    subtitle: product.type === "by-index" && product.index >= referenceNames.length ? /*#__PURE__*/ _jsx("p", {
                        className: "text-red-400 !opacity-90",
                        children: `Please create at least ${product.index + 1} products.`
                    }) : `Initiates checkout on ${product.type == "by-index" ? startCase(referenceNames[product.index]) : "selected"}`
                };
            case "set-product-index":
                const index = action.index;
                const products = referenceNames;
                return {
                    icon: /*#__PURE__*/ _jsx(SIcon, {
                        type: "product",
                        size: 16
                    }),
                    title: products[index] ? `Select ${getOrdinal(index + 1)} - ${startCase(products[index])}` : `Select ${getOrdinal(index + 1)} Product (Missing)`,
                    subtitle: `Set products.selectedIndex to ${action.index}`
                };
            case "set-state":
                return {
                    icon: /*#__PURE__*/ _jsx(SIcon, {
                        type: "code",
                        size: 16
                    }),
                    title: `${startCase(action.operation?.type || "set")} ${action.stateId.replace("state:", "")}`,
                    subtitle: action.operation?.type !== "toggle" && /*#__PURE__*/ _jsxs("span", {
                        children: [
                            `${action.operation?.type == "set" ? "to " : "by "}`,
                            action.value !== undefined ? templateValue(action.value.value) : /*#__PURE__*/ _jsx("span", {
                                className: "opacity-60",
                                children: "Empty"
                            })
                        ]
                    })
                };
            case "request-store-review":
                return {
                    icon: /*#__PURE__*/ _jsx(SIcon, {
                        type: "star",
                        size: 16
                    }),
                    title: "Request Store Review",
                    subtitle: `Request ${action.reviewType === "in-app" ? "rating prompt" : "written review"}`
                };
            case "set-attribute":
                const attributeCount = action.attributes?.length || 0;
                const firstAttr = action.attributes?.[0];
                return {
                    icon: /*#__PURE__*/ _jsx(SIcon, {
                        type: "edit",
                        size: 16
                    }),
                    title: "Set Attributes",
                    subtitle: attributeCount === 0 ? "No attributes configured" : attributeCount === 1 && firstAttr ? `${firstAttr.key} = ${templateValue(firstAttr.value)}` : `${attributeCount} attributes`
                };
            default:
                return;
        }
    })();
    const { getNode } = useEditorAPI();
    if (description && action.type === "set-state" && action.stateId.startsWith("state:node.")) {
        const stateIdParts = action.stateId.replace("state:node.", "").split(".");
        const node = getNode && getNode(createNodeId(stateIdParts[0]));
        if (node) {
            description.title = `${startCase(action.operation?.type || "set")} "${node.name}".${stateIdParts.slice(1).join(".")}`;
        }
    }
    return description ? /*#__PURE__*/ _jsxs("div", {
        className: "grid grid-cols-[auto,1fr] items-center gap-2",
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "pr-2 flex items-center justify-center border-r border-white/10",
                children: /*#__PURE__*/ _jsx("div", {
                    className: "w-[34px] h-[21px] flex items-center justify-center",
                    children: description.icon
                })
            }),
            /*#__PURE__*/ _jsxs("div", {
                children: [
                    /*#__PURE__*/ _jsx(Label, {
                        children: description.title
                    }),
                    description.subtitle ? /*#__PURE__*/ _jsx("div", {
                        className: "text-sm pl-2 opacity-50 max-w-[250px] truncate",
                        children: description.subtitle
                    }) : undefined
                ]
            })
        ]
    }) : /*#__PURE__*/ _jsx(_Fragment, {});
});
const InnerActionEditor = ({ action, onChange, platform })=>{
    if (!action) {
        return /*#__PURE__*/ _jsx(_Fragment, {});
    }
    switch(action.type){
        case "open-url":
            const a = action;
            return /*#__PURE__*/ _jsx(OpenUrlEditor, {
                value: a,
                onChange: onChange,
                platform: platform
            });
        case "close":
            return /*#__PURE__*/ _jsx(CloseEditor, {});
        case "restore":
            return /*#__PURE__*/ _jsx(RestoreEditor, {});
        case "custom-in-app":
            const d = action;
            return /*#__PURE__*/ _jsx(CustomEditor, {
                value: d,
                onChange: onChange
            });
        case "purchase":
            const e = action;
            return /*#__PURE__*/ _jsx(PurchaseEditor, {
                value: e,
                onChange: onChange
            });
        case "set-state":
            return /*#__PURE__*/ _jsx(SetStateEditor, {
                value: action,
                onChange: onChange
            });
        case "set-product-index":
            return /*#__PURE__*/ _jsx(SetProductIndexEditor, {
                value: action,
                onChange: onChange
            });
        case "custom-placement":
            return /*#__PURE__*/ _jsx(CustomPlacementEditor, {
                value: action,
                onChange: onChange
            });
        case "request-store-review":
            return /*#__PURE__*/ _jsx(RequestStoreReviewEditor, {
                value: action,
                onChange: onChange
            });
        case "set-attribute":
            return /*#__PURE__*/ _jsx(SetAttributeEditor, {
                value: action,
                onChange: onChange,
                platform: platform
            });
        default:
            return /*#__PURE__*/ _jsx("p", {
                className: "text-sm opacity-50 p-2",
                children: "Missing editor for type"
            });
    }
};
const Label = ({ children })=>{
    return /*#__PURE__*/ _jsx("div", {
        className: "text-sm pl-2 min-w-[85px] ",
        children: children
    });
};
// 1. open url
const OpenUrlEditor = ({ value, onChange, platform })=>{
    const { useVariablePickerItems, requiresLiquid, getRequiredPaths } = useEditorDependencyInjectionContext();
    const items = useVariablePickerItems();
    const [hasFocus, setHasFocus] = useState(false);
    const inputRef = useRef(null);
    const options = [
        "in-app-browser",
        "external",
        "deep-link"
    ];
    const filteredOptions = platform === "STRIPE" ? options.filter((option)=>option !== "deep-link") : options;
    const optionLabels = platform === "STRIPE" ? {
        "in-app-browser": "In Same Tab",
        external: "In New Tab"
    } : {
        "in-app-browser": "In App Browser",
        external: "External",
        "deep-link": "Deep Link"
    };
    const showProtocolWarning = value?.url && !value.url.startsWith("https://") && !value.url.startsWith("http://");
    const updateUrl = useCallback((url)=>{
        if (requiresLiquid(url)) {
            const requiredRecords = getRequiredPaths(url).map((x)=>SWStateRecordType.createId(x));
            onChange({
                ...value,
                url,
                rendering: {
                    type: "liquid",
                    requiredStateIds: requiredRecords
                }
            });
        } else {
            onChange({
                ...value,
                url,
                rendering: {
                    type: "literal"
                }
            });
        }
    }, [
        onChange,
        value,
        requiresLiquid,
        getRequiredPaths
    ]);
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Label, {
                children: "URL"
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-row items-center gap-1",
                children: [
                    /*#__PURE__*/ _jsx(SInputV2, {
                        ref: inputRef,
                        borderless: false,
                        value: value?.url,
                        onChange: (e)=>updateUrl(e.target.value),
                        placeholder: "URL",
                        onFocus: ()=>setHasFocus(true),
                        onBlur: ()=>setTimeout(()=>{
                                setHasFocus(false);
                            }, 1000)
                    }),
                    /*#__PURE__*/ _jsx(SVariablePicker, {
                        items: items,
                        asButton: true,
                        enableHotkey: hasFocus,
                        buttonRender: ({ isOpen, setIsOpen })=>/*#__PURE__*/ _jsx(SButton, {
                                isMinimal: true,
                                className: "!p-2 !h-[34px] !w-[34px] flex items-center justify-center",
                                onClick: ()=>setIsOpen(true),
                                children: /*#__PURE__*/ _jsx(SIcon, {
                                    type: "code",
                                    className: "text-brand-primary",
                                    size: 16
                                })
                            }),
                        didSelect: (item)=>{
                            let text = value?.url || "";
                            if (inputRef.current) {
                                const { selectionStart, selectionEnd } = inputRef.current;
                                const beforeText = text.substring(0, selectionStart || 0).trimEnd();
                                const textToInsert = item.liquid?.trim() || "";
                                const rhs = text.substring(selectionEnd || 0, text.length).trimStart();
                                const lhs = `${beforeText}${textToInsert}`;
                                const newPosition = lhs.length;
                                text = `${lhs}${rhs}`;
                                updateUrl(text);
                                setTimeout(()=>{
                                    inputRef.current?.focus();
                                    if (inputRef.current) {
                                        inputRef.current.selectionStart = newPosition;
                                        inputRef.current.selectionEnd = newPosition;
                                    }
                                }, 100);
                            } else {
                                updateUrl(`${value?.url || ""}${item.liquid?.trim()}`);
                            }
                        },
                        didClose: ()=>{
                            setTimeout(()=>{
                                inputRef.current?.focus();
                            }, 100);
                        }
                    })
                ]
            }),
            showProtocolWarning && /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx("div", {}),
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-sm text-white/50 -mt-1 opacity-80",
                        children: "Please include a protocol (https)!"
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(Label, {
                children: "Type"
            }),
            /*#__PURE__*/ _jsx(SDropdownV3, {
                options: filteredOptions.map((option)=>{
                    return {
                        label: optionLabels[option],
                        value: option
                    };
                }),
                onChange: (option)=>{
                    onChange({
                        ...value,
                        urlType: option.value
                    });
                },
                value: {
                    label: optionLabels[value.urlType],
                    value: value.urlType
                },
                borderless: false
            })
        ]
    });
};
// 2. close
const CloseEditor = ()=>{
    return /*#__PURE__*/ _jsx(_Fragment, {});
};
// 3. restore
const RestoreEditor = ()=>{
    return /*#__PURE__*/ _jsx(_Fragment, {});
};
// 4. custom
const CustomEditor = ({ value, onChange })=>{
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Label, {
                children: "Name"
            }),
            /*#__PURE__*/ _jsx(SInputV2, {
                borderless: false,
                value: value?.data,
                onChange: (e)=>onChange({
                        ...value,
                        data: e.target.value
                    }),
                placeholder: "Data"
            })
        ]
    });
};
// 4b. set attribute
// Hook to collect all existing user attribute keys from set-attribute actions
const useUserAttributeKeys = (currentValue)=>{
    const { useVariablePickerItems, useUserAttributeKeys: useUserAttributeKeysFromContext } = useEditorDependencyInjectionContext();
    // If the context provides a hook for getting user attribute keys (implemented in shared package),
    // use that. The hook returns a plain array of keys from all set-attribute actions in the editor.
    const keysFromContext = useUserAttributeKeysFromContext?.() || [];
    // Get all user.* variables from the variable picker (these are from template_variables)
    // Must call hook at top level, not inside useMemo
    const allVariables = useVariablePickerItems?.() || [];
    return useMemo(()=>{
        const keys = new Set();
        // Add keys from context (if available - this traverses all nodes in the editor)
        keysFromContext.forEach((key)=>keys.add(key));
        // Add keys from current action's attributes
        currentValue?.attributes?.forEach((attr)=>{
            if (attr.key) {
                keys.add(attr.key);
            }
        });
        // Add user.* variables from template_variables
        allVariables.forEach((item)=>{
            if (item.key.startsWith("user.")) {
                keys.add(item.key.replace("user.", ""));
            }
        });
        return Array.from(keys).sort();
    }, [
        allVariables,
        currentValue,
        keysFromContext
    ]);
};
const SetAttributeEditor = ({ value, onChange, platform })=>{
    const attributes = value?.attributes || [];
    const allKeys = useUserAttributeKeys(value);
    const addAttribute = ()=>{
        onChange({
            ...value,
            attributes: [
                ...attributes,
                {
                    key: "",
                    value: ""
                }
            ]
        });
    };
    const updateAttribute = (index, field, newValue)=>{
        const updated = [
            ...attributes
        ];
        updated[index] = {
            ...updated[index],
            [field]: newValue
        };
        onChange({
            ...value,
            attributes: updated
        });
    };
    const removeAttribute = (index)=>{
        onChange({
            ...value,
            attributes: attributes.filter((_, i)=>i !== index)
        });
    };
    const showSdkVersionWarning = platform === "IOS" || platform === "ANDROID";
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            attributes.map((attr, index)=>{
                // Exclude current attribute's key from suggestions
                const suggestions = allKeys.filter((k)=>k !== attr.key);
                return /*#__PURE__*/ _jsxs(React.Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx(Label, {
                            children: index === 0 ? "Key" : ""
                        }),
                        /*#__PURE__*/ _jsx(SWAttributeKeyPicker, {
                            value: attr.key,
                            onChange: (newKey)=>updateAttribute(index, "key", newKey),
                            suggestions: suggestions,
                            placeholder: "Key"
                        }),
                        /*#__PURE__*/ _jsx(Label, {
                            children: index === 0 ? "Value" : ""
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "flex flex-row items-center gap-1",
                            children: [
                                /*#__PURE__*/ _jsx(SInputV2, {
                                    borderless: false,
                                    value: attr.value,
                                    onChange: (e)=>updateAttribute(index, "value", e.target.value),
                                    placeholder: "Value"
                                }),
                                attributes.length > 1 && /*#__PURE__*/ _jsx("button", {
                                    onClick: ()=>removeAttribute(index),
                                    className: "text-white/40 hover:text-red-400 transition-colors flex-shrink-0 !p-2 !h-[34px] !w-[34px] flex items-center justify-center",
                                    title: "Remove attribute",
                                    children: /*#__PURE__*/ _jsx(SIcon, {
                                        type: "trash",
                                        size: 16
                                    })
                                })
                            ]
                        })
                    ]
                }, index);
            }),
            showSdkVersionWarning && /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx("div", {}),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "text-[13px] text-amber-500",
                        children: [
                            "Requires ",
                            platform === "IOS" ? "iOS SDK v4.10.7+" : "Android SDK v2.6.6+"
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(Label, {
                children: " "
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "flex justify-end",
                children: /*#__PURE__*/ _jsx(SButton, {
                    onClick: addAttribute,
                    children: "+ Add Attribute"
                })
            })
        ]
    });
};
// 5a. purchase by index
const PurchaseProductByIndexEditor = ({ value, onChange })=>{
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Label, {
                children: "Index"
            }),
            /*#__PURE__*/ _jsx(SInputV2, {
                borderless: false,
                value: value?.index,
                onChange: (e)=>onChange({
                        ...value,
                        index: parseInt(e.target.value)
                    }),
                placeholder: "Index"
            })
        ]
    });
};
// 5b. purchase by selected
const PurchaseProductBySelectedEditor = ({ value, onChange })=>{
    return /*#__PURE__*/ _jsx(_Fragment, {});
};
function getOrdinal(n) {
    if (n % 100 >= 11 && n % 100 <= 13) {
        // Numbers like 11, 12, 13, 111, 112, 113, etc.
        return n + "th";
    }
    switch(n % 10){
        case 1:
            return n + "st";
        case 2:
            return n + "nd";
        case 3:
            return n + "rd";
        default:
            return n + "th";
    }
}
// 5. purchase
const PurchaseEditor = track(function PurchaseEditor({ value, onChange }) {
    const { products: { getProductReferenceNames } } = useEditorAPI();
    const referenceNames = getProductReferenceNames().get();
    const reference = value.reference;
    // cast to the correct type
    const action = reference;
    const props = {
        value: action,
        onChange: (reference)=>{
            onChange({
                ...value,
                reference
            });
        }
    };
    const currentIndex = action.type == "by-index" ? action.index : null;
    let options = [
        ...referenceNames.map((r, i)=>getOrdinal(i + 1) + ` - ${startCase(r)}`)
    ];
    if (currentIndex && currentIndex >= referenceNames.length) {
        options = [
            ...options,
            getOrdinal(currentIndex + 1) + " Product (Missing)"
        ];
    }
    options = [
        ...options,
        "Selected"
    ];
    console.log("[!] options", action.type, options, currentIndex, referenceNames.length + 1);
    // ["primary", "secondary", "tertiary", "selected"]
    const onChangeOption = (option)=>{
        if (option.toLowerCase() == "selected") {
            onChange({
                ...value,
                reference: {
                    type: "by-selected"
                }
            });
        } else {
            onChange({
                ...value,
                reference: {
                    type: "by-index",
                    index: options.indexOf(option)
                }
            });
        }
    };
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Label, {
                children: "Product"
            }),
            /*#__PURE__*/ _jsx(SDropdownV3, {
                options: options.map((option)=>{
                    return {
                        label: option,
                        value: option
                    };
                }),
                onChange: (option)=>{
                    onChangeOption(option.value);
                },
                value: (()=>{
                    return action.type == "by-index" ? {
                        label: options[action.index],
                        value: options[action.index]
                    } : {
                        label: "Selected",
                        value: "Selected"
                    };
                })(),
                borderless: false
            }),
            /*#__PURE__*/ _jsx("p", {
                className: "text-sm text-red-700 opacity-80 col-span-2",
                children: currentIndex && currentIndex >= referenceNames.length ? `Invalid product index, include at least ${currentIndex + 1} products.` : ""
            })
        ]
    });
});
const SetStateEditor = ({ value, onChange })=>{
    const { useVariablePickerItems } = useEditorDependencyInjectionContext();
    const items = useVariablePickerItems({
        id: "click-behavior"
    }).filter((e)=>e.key == "state" || e.key == "params" || e.key == "element");
    console.log("[!] items", items);
    const stateId = value.stateId;
    const operation = value.operation;
    const state = flattenTree(items).find((e)=>e.state?.id == stateId)?.state;
    const stateType = state?.defaultValue?.type === "value-type" ? state?.defaultValue?.value : state?.defaultValue?.type || "css-string";
    const Component = EditorForValueType[stateType] || EditorForValueType["css-string"];
    const allowedOperationsByType = {
        "variable-number": [
            "set",
            "increment",
            "decrement"
        ],
        "variable-boolean": [
            "set",
            "toggle"
        ],
        "variable-string": [
            "set"
        ]
    };
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Label, {
                children: "Variable"
            }),
            /*#__PURE__*/ _jsx(SVariablePicker, {
                items: items,
                asButton: true,
                buttonRender: ({ isOpen, setIsOpen })=>{
                    return /*#__PURE__*/ _jsx(SDropdownV3, {
                        options: [],
                        onChange: ()=>{},
                        customOnClick: ()=>{
                            setIsOpen(true);
                        },
                        value: value.stateId ? {
                            label: value.stateId.replace("state:", ""),
                            value: value.stateId
                        } : undefined,
                        borderless: false,
                        labelClassName: "!max-w-[200px]"
                    });
                },
                didSelect: (item)=>{
                    // Don't allow you to set locked state
                    if (item.state?.locked === true) {
                        return;
                    }
                    let allowedOperations = allowedOperationsByType[item.state?.defaultValue.type || "css-string"];
                    if (allowedOperations === undefined && item.state?.defaultValue.type === "value-type") {
                        allowedOperations = allowedOperationsByType[item?.state.defaultValue.value];
                    }
                    let newOperation = value.operation;
                    // TODO: @ssalbdivad tried to fix these type errors but they seem to represent genuine issues
                    // with allowedOperationsByType and the way it's being used. Should fix- it seems like it would often
                    // be falling back to undefined here?
                    if (!allowedOperations.includes(value.operation?.type)) {
                        newOperation = {
                            // TODO: continued fix allowedOperationsByType
                            type: allowedOperations[0]
                        };
                    }
                    item.state?.id ? onChange({
                        ...value,
                        stateId: item.state?.id,
                        operation: newOperation
                    }) : undefined;
                },
                didClose: ()=>{
                // setTimeout(() => {
                //   textAreaRef.current?.focus()
                // }, 100)
                }
            }),
            stateType != "variable-string" && /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(Label, {
                        children: "Operation"
                    }),
                    /*#__PURE__*/ _jsx(SDropdownV3, {
                        value: value.operation?.type || "set",
                        borderless: false,
                        options: (allowedOperationsByType[stateType] || allowedOperationsByType["variable-string"]).map((option)=>{
                            return {
                                label: startCase(option),
                                value: option
                            };
                        }),
                        onChange: (option)=>{
                            onChange({
                                ...value,
                                operation: {
                                    // TODO: continued fix allowedOperationsByType
                                    type: option.value
                                },
                                // update the value to undefined if it's toggle
                                ...option.value == "toggle" ? {
                                    value: undefined
                                } : {},
                                // update the value to 1 if its increment or decrement
                                ...option.value == "increment" || option.value == "decrement" ? {
                                    value: {
                                        type: "variable-number",
                                        value: 1
                                    }
                                } : {}
                            });
                        }
                    })
                ]
            }),
            (value.operation?.type === undefined || value.operation?.type === "set" || stateType == "variable-string") && /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(Label, {
                        children: "Value"
                    }),
                    /*#__PURE__*/ _jsx(Component, {
                        value: value?.value,
                        onChangeValue: (v)=>{
                            onChange({
                                ...value,
                                value: v
                            });
                        }
                    })
                ]
            })
        ]
    });
};
// const editor = useSWEditor()
// const stateValueId = SWStateValueRecordType.createId(state.id.replace(/^state:/, ""))
// const stateValue = useValue("state value", () => editor.swStore.get(stateValueId), [
//   editor,
//   state.id,
// ])
// const stateType =
//   stateValue?.value?.type || state.defaultValue?.type || state.valueType?.value || "variable-string"
// const Component = EditorForValueType[stateType] || EditorForValueType["css-string"]
// return (
//   <div className="grid grid-cols-1 items-center justify-stretch">
//     <Component
//       value={stateValue?.value as any}
//       onChangeValue={(value) => {
//         editor.swStore.put([
//           {
//             id: SWStateValueRecordType.createId(state.id.replace(/^state:/, "")),
//             typeName: SWStateValueRecordType.typeName,
//             value,
//           },
//         ])
//       }}
//     />
//   </div>
// )
// TODO: Fix this!
const SetProductIndexEditor = ({ value, onChange })=>{
    // const options = ["primary", "secondary", "tertiary"].map((option, index) => {
    //   return {
    //     label: startCase(option),
    //     value: `${index}`,
    //   }
    // })
    const { products: { getProductReferenceNames } } = useEditorAPI();
    const referenceNames = getProductReferenceNames().get();
    let productLabels = referenceNames.map((r, i)=>getOrdinal(i + 1) + ` - ${startCase(r)}`);
    const currentIndex = value.index;
    if (currentIndex && currentIndex >= referenceNames.length) {
        productLabels.push(getOrdinal(currentIndex + 1) + " Product (Missing)");
    }
    const options = productLabels.map((option, index)=>{
        return {
            label: option,
            value: `${index}`
        };
    });
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Label, {
                children: "Product"
            }),
            /*#__PURE__*/ _jsx(SDropdownV3, {
                options: options,
                onChange: (option)=>{
                    onChange({
                        ...value,
                        index: parseInt(option.value)
                    });
                },
                value: options.find((option)=>option.value == value.index.toString()),
                borderless: false
            })
        ]
    });
};
// 4. custom
const CustomPlacementEditor = ({ value, onChange })=>{
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Label, {
                children: "Name"
            }),
            /*#__PURE__*/ _jsx(SInputV2, {
                borderless: false,
                value: value?.name,
                onChange: (e)=>onChange({
                        ...value,
                        name: e.target.value
                    }),
                placeholder: "NewPlacement"
            })
        ]
    });
};
const RequestStoreReviewEditor = ({ value, onChange })=>{
    const { getApplicationPlatform, getApplicationId } = useEditorAPI();
    const applicationPlatform = getApplicationPlatform && getApplicationPlatform();
    const applicationId = getApplicationId && getApplicationId();
    const options = [
        "in-app",
        "external"
    ];
    const optionLabels = {
        "in-app": "Rating Prompt",
        external: "Written Review"
    };
    const showIosWarning = value.reviewType === "external" && applicationPlatform === "IOS";
    const showIosRatingPromptNote = value.reviewType === "in-app" && applicationPlatform === "IOS";
    const showSdkVersionWarning = applicationPlatform === "IOS" || applicationPlatform === "ANDROID";
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Label, {
                children: "Type"
            }),
            /*#__PURE__*/ _jsx(SDropdownV3, {
                options: options.map((option)=>{
                    return {
                        label: optionLabels[option],
                        value: option
                    };
                }),
                onChange: (option)=>{
                    onChange({
                        ...value,
                        reviewType: option.value
                    });
                },
                value: {
                    label: optionLabels[value.reviewType],
                    value: value.reviewType
                },
                borderless: false
            }),
            showSdkVersionWarning && /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx("div", {}),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "text-[13px] text-amber-500 -mt-1",
                        children: [
                            "Requires ",
                            applicationPlatform === "IOS" ? "iOS SDK v4.8.1+" : "Android SDK v2.5.3+"
                        ]
                    })
                ]
            }),
            showIosWarning && /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx("div", {}),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "text-[13px] text-muted-foreground -mt-2.5",
                        children: [
                            "Make sure you have an",
                            " ",
                            /*#__PURE__*/ _jsx("a", {
                                href: "#",
                                onClick: (e)=>{
                                    e.preventDefault();
                                    if (applicationId) {
                                        const settingsURL = `/applications/${applicationId}/settings`;
                                        window.open(settingsURL, "_blank");
                                    }
                                },
                                className: "text-muted-foreground hover:text-foreground transition-colors underline",
                                children: "Apple App ID"
                            }),
                            " ",
                            "configured in your application settings."
                        ]
                    })
                ]
            }),
            showIosRatingPromptNote && /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx("div", {}),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "text-[13px] text-muted-foreground -mt-2.5",
                        children: [
                            "Rating prompt is controlled by the system and may not show.",
                            " ",
                            /*#__PURE__*/ _jsx("a", {
                                href: "https://developer.apple.com/design/human-interface-guidelines/ratings-and-reviews",
                                target: "_blank",
                                rel: "noopener noreferrer",
                                className: "text-muted-foreground hover:text-foreground transition-colors underline",
                                children: "Learn more"
                            })
                        ]
                    })
                ]
            })
        ]
    });
};
